// ---- Pure fishing sim (plan 10; angling reworked by the Living Waters plan) ----
// Three machine families + the netting soak stepper, extracted the same way
// src/sim/combat.ts was (plan 13): the game server ticks these, and the studio
// Skill-Sim fishing driver imports the LITERAL same rules. Same purity rule as
// combat.ts — no index.ts / src/server/* imports, no document/window/Bun.*/
// process.env. rng arrives as an ARGUMENT (`Rng`), geometry as a water
// predicate (`FishNav`), species params as data (`FishParams`, structurally the
// item doc's `fish` block) — so the parity test stays deterministic.
//
//   1. FISH WANDER  — stepFishWander: idle → waypoint → cruise/dart inside the
//      water zone. Runs for every un-engaged fish in an awake water.
//   2. THE ENCOUNTER — one fish vs one bobber: noticeCheck picks the fish,
//      then stepEncounter drives approach → investigate (may lose interest) →
//      nibble ×N → BITE (grab window). tryHook judges a player pull; a pull
//      during a nibble never hooks (it may spook). releaseFish is the ONE
//      disengage rule (dart away + wary bump + bobber cooldown).
//   3. THE REEL MINIGAME — genReelPattern (seed-deterministic: the same seed
//      builds the identical pattern on server and client) + the ReelJudge
//      (per-arrow tension arithmetic; the server re-runs it as the
//      authoritative verdict, the client mirrors it for display).
//
// This module is also the ONE home for the fishing-economy DEFAULTS (the
// meaning of each number + the values the deterministic tests pin). The LIVE
// values are content: content/tunables/fishing.json overrides these
// field-by-field via src/tunables.ts (FISHING_TUNING) — the server handlers,
// the client HUD, and the studio Skill-Sim all read THAT, never these
// constants directly (and never re-declare one locally). The studio's
// Apply → JSON writes the doc, which is how tuned pacing ships to the game.

import { applyLaneResult, forceResolveLane, genLanePattern, randRange, startLaneJudge } from "./lane";
import type { LaneArrow, LaneDir, LaneEvent, LaneJudge, LanePattern, Rng } from "./lane";

/** Caller-supplied randomness (Math.random in the game, seeded in tests). */
export type { Rng } from "./lane";

/** Caller-supplied geometry: is (x,z) swimmable for this fish? The server
 *  passes zone-radius ∧ real water depth; the studio passes its pond discs. */
export type FishNav = { inWater(x: number, z: number): boolean };

// ---- Tuning DEFAULTS shared by both sides ------------------------------------
// Angling (kept from CP1):
export const CAST_RANGE = 12;       // max freeform reach to a valid water point
export const CAST_CD = 1.0;         // seconds between accepted cast intents (rate gate)
export const WATER_MARGIN = 0.4;    // castable/swimmable water: heightAt < WATER_LEVEL - WATER_MARGIN

// Fish wander:
export const CRUISE_SPEED = 0.8;    // u/s default wander speed (fish block cruiseSpeed overrides)
export const DART_SPEED = 2.4;      // u/s default spook/burst speed (fish block dartSpeed)
export const IDLE_MIN = 1.0;        // s a fish rests between waypoints (uniform roll)
export const IDLE_MAX = 4.0;
export const DART_CHANCE = 0.08;    // chance a wander leg is a dart (flavor burst)

// The encounter:
export const NOTICE_RADIUS = 6;     // u — a wandering fish inside this of the bobber may notice
export const NOTICE_EVERY = 0.8;    // s between the cast's notice scans (caller-side timer)
export const CURIOSITY = 0.6;       // default notice-roll base (fish block curiosity)
export const PATIENCE = 5;          // s default investigate budget (fish block patience)
export const APPROACH_DIST = 0.7;   // u — close enough to start investigating
export const INVESTIGATE_MIN = 3;   // s investigate-budget roll bounds (× patience/PATIENCE)
export const INVESTIGATE_MAX = 8;
export const NIBBLE_MIN = 1;        // nibble tugs before the bite (uniform roll, inclusive)
export const NIBBLE_MAX = 3;
export const NIBBLE_DUR = 0.5;      // s a nibble tug holds
export const NIBBLE_GAP_MIN = 0.8;  // s between investigate decisions (nibble or bite)
export const NIBBLE_GAP_MAX = 2.5;
export const NIBBLE_SPOOK = 0.5;    // chance a pull DURING a nibble spooks the fish
export const PULL_SPOOK = 0.25;     // chance a pull while approaching/investigating spooks
export const BITE_WINDOW = 0.9;     // s grab window (× box reelWindowMul)
export const SPOOK_CD = 8;          // s a spooked fish ignores bobbers
export const LOSE_CD = 4;           // s a bored fish ignores bobbers
export const WARY_BUMP = 0.35;      // wary added on a spook (half on mere boredom)
export const WARY_DECAY = 0.02;     // wary shed per second while wandering

// Population:
export const FISH_RESPAWN = 120;         // s for one caught fish to respawn (map waters[] respawn overrides)
export const SHADOW_DEPTH_OFFSET = 0.05; // u below the surface the client draws shadows

// The reel minigame:
export const REEL_ARROWS_BASE = 8;      // arrows in the pattern before tier/fight scaling
export const REEL_ARROWS_PER_TIER = 4;  // + arrows per fish tier
export const REEL_GAP_BASE = 0.9;       // s between arrows at tier 0
export const REEL_GAP_MIN = 0.45;       // hard gap floor (playability)
export const REEL_SPEED_RAMP = 0.15;    // gap divisor grows 1 + tier×this
export const REEL_HIT_WINDOW = 0.18;    // ± s a press counts as a hit (× box reelWindowMul)
export const REEL_MISS_TENSION = 0.2;   // tension added per missed arrow (1.0 = escape)
export const REEL_HIT_RELIEF = 0.05;    // tension shed per hit arrow
export const REEL_INPUT_SLACK = 1.5;    // s the server tolerates a reelInput arriving early
export const REEL_DEADLINE_PAD = 3;     // s past the pattern's end before the server force-resolves

// Netting (untouched by the rework):
export const SOAK_TIME = 540;       // seconds a net soaks before it's ready (× box soakMul)
export const NET_CAPACITY_MIN = 5;  // fish per haul (uniform roll, rng outside)
export const NET_CAPACITY_MAX = 8;
export const NET_COUNT = 1;         // deployed nets allowed at once (+ box netCount)
export const NET_RANGE = 7;         // max reach to deploy / collect a net (xz units)

/** Catch tables the RUNTIME references by hardcoded id (netting hauls only —
 *  angling now lands the literal fish that bit). validate.ts asserts every one
 *  of these resolves to a content/catch-tables doc. */
export const REQUIRED_CATCH_TABLES = ["netting-bulk"] as const;

/** Angling XP per landed fish (~10 avg → ~10 catches to 100). */
export const ANGLING_XP: Record<string, number> = { minnow: 5, perch: 10, trout: 20, pike: 40 };
export const ANGLING_XP_DEFAULT = 5;
/** Netting XP per fish landed in a haul (a 5–8 haul → 15–24). */
export const NETTING_XP_PER_FISH = 3;
/** Fishmonger standing deeds: every branch feeds the same patron. */
export const FISHMONGER_PER_CATCH = 10;
export const FISHMONGER_PER_HAUL = 15;

// ---- Species params ------------------------------------------------------------
// Structurally IDENTICAL to the item doc's `fish` block (items/define.ts
// FishSpec) — content arrives as an argument, no adapter layer.

export type FishParams = {
  /** Hook tier: the fish only takes a hook when the angler's tableTier ≥ this. */
  tier: number;
  /** Shadow/model scale × (client visual; the sim never reads it). */
  size?: number;
  cruiseSpeed?: number;
  dartSpeed?: number;
  /** 0..1 notice-roll base. */
  curiosity?: number;
  /** s investigate budget base. */
  patience?: number;
  nibblesMin?: number;
  nibblesMax?: number;
  /** Reel-fight difficulty × (arrows + speed). */
  fightMul?: number;
};

// ---- The fish record -------------------------------------------------------------

export type FishState =
  | "idle" | "cruise" | "dart"                       // wandering (stepFishWander)
  | "approach" | "investigate" | "nibble" | "bite"   // engaged (stepEncounter)
  | "hooked";                                        // the reel fight (judge owns the outcome)

/** The wire/render projection: idle+cruise collapse to "swim". */
export type FishWireState = "swim" | "dart" | "approach" | "investigate" | "nibble" | "bite" | "hooked";

export type SimFish = {
  id: string;
  /** Species — the fish ITEM type; landing this fish banks exactly this item. */
  type: string;
  x: number;
  z: number;
  ry: number;
  state: FishState;
  /** Seconds left in the current state (idle rest, nibble hold, bite window). */
  t: number;
  /** Current waypoint (wander + release dart). */
  tx: number;
  tz: number;
  /** The water zone this fish lives in (waypoints stay inside home.r). */
  home: { x: number; z: number; r: number };
  /** 0..1 dynamic wariness — bumped by spooks, decays while wandering; scales
   *  the notice roll down. Distinct from the species' static curiosity. */
  wary: number;
  /** Seconds this fish ignores bobbers (post-spook / post-boredom). */
  cd: number;
};

/** The freshCombatant twin — one home for the SimFish field set. */
export function freshFish(id: string, type: string, x: number, z: number, home: SimFish["home"]): SimFish {
  return { id, type, x, z, ry: 0, state: "idle", t: 0, tx: x, tz: z, home, wary: 0, cd: 0 };
}

export function wireState(f: SimFish): FishWireState {
  return f.state === "idle" || f.state === "cruise" ? "swim" : f.state;
}

const randIntIncl = (rng: Rng, min: number, max: number) => min + Math.floor(rng() * (max - min + 1));

const TURN_CRUISE = 2.0; // rad/s — lazy arcs
const TURN_FAST = 6.0;   // rad/s — darts + the approach
const ARRIVE_DIST = 0.15;

/** Rate-limited turn toward a point (fish steer, they don't snap). */
function turnToward(f: SimFish, tx: number, tz: number, rate: number, dt: number): void {
  const want = Math.atan2(tx - f.x, tz - f.z);
  let d = want - f.ry;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  f.ry += Math.max(-rate * dt, Math.min(rate * dt, d));
}

/** Advance along the current heading, refusing to leave water. Returns false
 *  when blocked (the caller re-picks). */
function swimForward(f: SimFish, speed: number, dt: number, nav: FishNav): boolean {
  const nx = f.x + Math.sin(f.ry) * speed * dt;
  const nz = f.z + Math.cos(f.ry) * speed * dt;
  if (!nav.inWater(nx, nz)) return false;
  f.x = nx;
  f.z = nz;
  return true;
}

// ---- 1. Fish wander ---------------------------------------------------------------

/** Advance one un-engaged fish by dt: rest → pick a waypoint (≤12 rejection
 *  samples through nav.inWater, inside home.r; nowhere to go = retry shortly) →
 *  cruise (or dart) toward it → arrive → rest. Also decays wary + the bobber
 *  cooldown. Engaged fish (approach..hooked) are a no-op here — the encounter
 *  drives them. */
export function stepFishWander(f: SimFish, p: FishParams, dt: number, nav: FishNav, rng: Rng, waryDecay: number = WARY_DECAY): void {
  f.wary = Math.max(0, f.wary - waryDecay * dt);
  f.cd = Math.max(0, f.cd - dt);
  if (f.state !== "idle" && f.state !== "cruise" && f.state !== "dart") return;

  if (f.state === "idle") {
    f.t -= dt;
    if (f.t > 0) return;
    for (let i = 0; i < 12; i++) {
      const a = rng() * Math.PI * 2;
      const d = Math.sqrt(rng()) * f.home.r; // sqrt = uniform over the disc
      const x = f.home.x + Math.sin(a) * d;
      const z = f.home.z + Math.cos(a) * d;
      if (!nav.inWater(x, z)) continue;
      f.tx = x;
      f.tz = z;
      f.state = rng() < DART_CHANCE ? "dart" : "cruise";
      return;
    }
    f.t = 0.5; // fully landlocked sample run — rest and retry
    return;
  }

  const dart = f.state === "dart";
  const speed = dart ? (p.dartSpeed ?? DART_SPEED) : (p.cruiseSpeed ?? CRUISE_SPEED);
  turnToward(f, f.tx, f.tz, dart ? TURN_FAST : TURN_CRUISE, dt);
  if (!swimForward(f, speed, dt, nav) || Math.hypot(f.tx - f.x, f.tz - f.z) <= ARRIVE_DIST) {
    f.state = "idle";
    f.t = randRange(rng, IDLE_MIN, IDLE_MAX);
  }
}

// ---- 2. The encounter ----------------------------------------------------------------
// One fish vs one bobber. The CALLER owns the pairing bookkeeping (a cast holds
// at most one fishId; an engaged fish is excluded from every other scan) and
// applies the events; the machines mutate only the fish + encounter records.

/** The subset of FISHING_TUNING the encounter machines read (structural — the
 *  callers pass FISHING_TUNING / a sandbox copy straight in). */
export type EncounterTuning = {
  noticeRadius: number;
  investigateMin: number;
  investigateMax: number;
  nibbleMin: number;
  nibbleMax: number;
  nibbleDur: number;
  nibbleGapMin: number;
  nibbleGapMax: number;
  nibbleSpook: number;
  pullSpook: number;
  spookCd: number;
  loseCd: number;
  waryBump: number;
};

/** THE notice rule — the caller runs it per candidate on its notice timer
 *  (nearest first; first pass wins). `attract` is the rod's attractMul × any
 *  future lure factor; `maxTier` is the angler's folded tableTier. */
export function noticeCheck(
  f: SimFish, p: FishParams, bobber: { x: number; z: number },
  maxTier: number, attract: number, tun: EncounterTuning, rng: Rng,
): boolean {
  if (f.state !== "idle" && f.state !== "cruise" && f.state !== "dart") return false;
  if (f.cd > 0) return false;
  if (p.tier > maxTier) return false;
  if (Math.hypot(f.x - bobber.x, f.z - bobber.z) > tun.noticeRadius) return false;
  return rng() < (p.curiosity ?? CURIOSITY) * (1 - f.wary) * attract;
}

export type Encounter = {
  fishId: string;
  /** Nibble tugs left before the fish commits to the bite. */
  nibblesLeft: number;
  /** The armed grab window (s) — stamped at start (tun.biteWindow × box mul). */
  biteWindow: number;
  /** Investigate: seconds to the next decision (nibble / bite / leave check). */
  gapT: number;
  /** Investigate budget left — lapse = the fish loses interest. */
  patienceT: number;
};

export type EncounterEvent =
  | { kind: "nibble" }                                // tug edge — owner FX + a false-pull trap
  | { kind: "bite" }                                  // grab window armed
  | { kind: "lost"; why: "interest" | "spooked" };    // fish released itself; the CAST stays live

/** Engage a fish on a bobber: it starts swimming in. Rolls the nibble count +
 *  the investigate budget (patience × 0.75..1.25). */
export function startEncounter(f: SimFish, p: FishParams, biteWindow: number, tun: EncounterTuning, rng: Rng): Encounter {
  f.state = "approach";
  f.t = 0;
  const nMin = p.nibblesMin ?? tun.nibbleMin;
  const nMax = Math.max(nMin, p.nibblesMax ?? tun.nibbleMax);
  return {
    fishId: f.id,
    nibblesLeft: randIntIncl(rng, nMin, nMax),
    biteWindow,
    gapT: randRange(rng, tun.nibbleGapMin, tun.nibbleGapMax),
    patienceT: randRange(rng, tun.investigateMin, tun.investigateMax) * ((p.patience ?? PATIENCE) / PATIENCE),
  };
}

const ORBIT_R = 0.85;    // u — investigate circling radius
const ORBIT_SPEED = 0.9; // rad/s

/** Drive the engaged fish. Emits at most one event per step:
 *  approach     swim to the bobber (arrive ≤ APPROACH_DIST → investigate).
 *  investigate  slow orbit; every gapT: patience spent → lost:"interest",
 *               else nibble (while any left) or the BITE.
 *  nibble       hold nibbleDur, then back to investigate.
 *  bite         the grab window; lapse → lost:"spooked".
 *  hooked       nothing to step — the reel judge owns the outcome.
 *  On a `lost` event the fish has already released itself (releaseFish); the
 *  caller clears its cast bookkeeping + notifies the owner. */
export function stepEncounter(
  enc: Encounter, f: SimFish, p: FishParams, bobber: { x: number; z: number },
  dt: number, tun: EncounterTuning, rng: Rng,
): EncounterEvent[] {
  switch (f.state) {
    case "approach": {
      turnToward(f, bobber.x, bobber.z, TURN_FAST, dt);
      const speed = p.cruiseSpeed ?? CRUISE_SPEED;
      f.x += Math.sin(f.ry) * speed * dt; // the bobber is in open water — no nav guard on the short run in
      f.z += Math.cos(f.ry) * speed * dt;
      if (Math.hypot(bobber.x - f.x, bobber.z - f.z) <= APPROACH_DIST) f.state = "investigate";
      return NO_ENC_EVENTS;
    }
    case "investigate": {
      const a = Math.atan2(f.x - bobber.x, f.z - bobber.z) + ORBIT_SPEED * dt;
      f.x = bobber.x + Math.sin(a) * ORBIT_R;
      f.z = bobber.z + Math.cos(a) * ORBIT_R;
      f.ry = a + Math.PI / 2;
      enc.patienceT -= dt;
      if (enc.patienceT <= 0) {
        releaseFish(f, bobber, false, tun, rng);
        return LOST_INTEREST;
      }
      enc.gapT -= dt;
      if (enc.gapT > 0) return NO_ENC_EVENTS;
      enc.gapT = randRange(rng, tun.nibbleGapMin, tun.nibbleGapMax);
      if (enc.nibblesLeft > 0) {
        enc.nibblesLeft--;
        f.state = "nibble";
        f.t = tun.nibbleDur;
        return NIBBLE_EVENTS;
      }
      f.state = "bite";
      f.t = enc.biteWindow;
      return BITE_EVENTS;
    }
    case "nibble": {
      f.t -= dt;
      if (f.t <= 0) f.state = "investigate";
      return NO_ENC_EVENTS;
    }
    case "bite": {
      f.t -= dt;
      if (f.t > 0) return NO_ENC_EVENTS;
      releaseFish(f, bobber, true, tun, rng); // grabbed, got nothing — bolts
      return LOST_SPOOKED;
    }
    default:
      return NO_ENC_EVENTS; // hooked (judge owns it) or a desynced wander state
  }
}

const NO_ENC_EVENTS: EncounterEvent[] = [];
const NIBBLE_EVENTS: EncounterEvent[] = [{ kind: "nibble" }];
const BITE_EVENTS: EncounterEvent[] = [{ kind: "bite" }];
const LOST_INTEREST: EncounterEvent[] = [{ kind: "lost", why: "interest" }];
const LOST_SPOOKED: EncounterEvent[] = [{ kind: "lost", why: "spooked" }];

/** Judge a player PULL against the engaged fish:
 *  bite        → hooked (the reel fight starts).
 *  nibble      → the false-pull trap: never hooks; may spook (roll × nothing —
 *                nibbleSpook is already the per-pull chance; wariness shaped the
 *                notice, not this).
 *  approach/investigate → a line twitch; small pullSpook chance.
 *  "spooked" means the fish has already released itself; "ignored" means the
 *  encounter continues untouched. */
export function tryHook(
  enc: Encounter, f: SimFish, bobber: { x: number; z: number },
  tun: EncounterTuning, rng: Rng,
): "hooked" | "spooked" | "ignored" {
  if (f.state === "bite") {
    f.state = "hooked";
    return "hooked";
  }
  const chance = f.state === "nibble" ? tun.nibbleSpook : tun.pullSpook;
  if (rng() < chance) {
    releaseFish(f, bobber, true, tun, rng);
    return "spooked";
  }
  return "ignored";
}

/** THE one disengage rule — bite-lapse, boredom, a spooking pull, a lost reel
 *  fight, and every cast teardown (Esc/KO/disconnect/transfer) route through
 *  here: dart away from the bobber, bump wary (half for mere boredom), and
 *  stamp the bobber-ignoring cooldown. */
export function releaseFish(f: SimFish, from: { x: number; z: number }, spooked: boolean, tun: EncounterTuning, rng: Rng): void {
  f.state = "dart";
  f.t = 0;
  f.wary = Math.min(1, f.wary + (spooked ? tun.waryBump : tun.waryBump * 0.5));
  f.cd = spooked ? tun.spookCd : tun.loseCd;
  const away = Math.atan2(f.x - from.x, f.z - from.z) + (rng() - 0.5) * 0.8;
  const d = 2 + rng() * 2;
  f.tx = f.x + Math.sin(away) * d;
  f.tz = f.z + Math.cos(away) * d;
  f.ry = Math.atan2(f.tx - f.x, f.tz - f.z);
}

// ---- 3. The reel minigame -----------------------------------------------------------
// Pattern generation is SEED-deterministic: the server rolls one seed, sends
// it in `fishOn`, and both sides expand the identical pattern from it. The
// judge is the shared tension arithmetic — the client mirrors it for display,
// the server's copy is the verdict. The generic prompt-lane machinery
// (pattern expansion + meter judge) lives in sim/lane.ts since plan 25 —
// cooking's pan minigame shares it — and this module keeps the reel-named
// surface as thin delegates, so every existing import site (reel.ts, the
// server handlers, the studio driver, the parity tests) is untouched.

export type ReelTuning = {
  reelArrowsBase: number;
  reelArrowsPerTier: number;
  reelGapBase: number;
  reelGapMin: number;
  reelSpeedRamp: number;
  reelMissTension: number;
  reelHitRelief: number;
};

/** W=0 A=1 S=2 D=3 (the lane order everywhere). */
export type ReelDir = LaneDir;
export type ReelArrow = LaneArrow;
export type ReelPattern = LanePattern;

/** Deterministic PRNG — re-exported so tests and the studio bot can share it. */
export { mulberry32 } from "./lane";

/** Build the arrow pattern for a hooked fish. Count scales with tier + the
 *  species' fightMul; pace tightens with tier and fightMul but never below
 *  reelGapMin; ±20% gap jitter; no immediate same-direction repeats. */
export function genReelPattern(seed: number, fish: { tier: number; fightMul?: number }, tun: ReelTuning): ReelPattern {
  const fight = fish.fightMul ?? 1;
  const count = Math.max(3, Math.round((tun.reelArrowsBase + fish.tier * tun.reelArrowsPerTier) * fight));
  const gapEff = tun.reelGapBase / (1 + fish.tier * tun.reelSpeedRamp) / fight;
  return genLanePattern(seed, count, gapEff, tun.reelGapMin);
}

/** hitWindow = ± s a press counts (client presentation AND the half-width of
 *  the server's honest-arrival check); tension 0..1 — reaching 1 loses the fish. */
export type ReelJudge = LaneJudge;

export type ReelEvent = LaneEvent;

export function startReelJudge(pattern: ReelPattern, hitWindow: number): ReelJudge {
  return startLaneJudge(pattern, hitWindow);
}

/** Judge the next arrow. Escapes the moment tension reaches 1 (mid-pattern);
 *  lands when the last arrow resolves with tension still under it. */
export function applyArrowResult(j: ReelJudge, hit: boolean, tun: ReelTuning): ReelEvent[] {
  return applyLaneResult(j, hit, { miss: tun.reelMissTension, relief: tun.reelHitRelief });
}

/** Deadline resolution (AFK / stalled input): every unjudged arrow is a miss. */
export function forceResolve(j: ReelJudge, tun: ReelTuning): ReelEvent[] {
  return forceResolveLane(j, { miss: tun.reelMissTension, relief: tun.reelHitRelief });
}

// ---- Weighted catch tables (netting hauls) ---------------------------------------
// A catch-tables doc is a weighted SINGLE-pick list (a haul = several
// independent picks). Pure over a passed roll ∈ [0,1) so tests stay
// deterministic; the caller supplies Math.random().

export type CatchEntry = { type: string; weight: number };

/** Pick one entry by weight from `roll` ∈ [0,1). The one copy of the pick rule. */
export function pickWeighted(entries: CatchEntry[], roll: number): string {
  let total = 0;
  for (const e of entries) total += e.weight;
  let at = roll * total;
  for (const e of entries) {
    at -= e.weight;
    if (at < 0) return e.type;
  }
  return entries[entries.length - 1]!.type; // roll === 1 - ε rounding
}

// ---- Netting soak stepper -------------------------------------------------------
// The server keys deployed nets on ABSOLUTE epoch `readyAt` (they persist across
// downtime, like NPC respawns) — this dt-stepper is the studio/test half of the
// same rule, for arenas with no wall clock.

export type NetState = {
  /** Seconds of soak left (0 = ready to haul). */
  soakLeft: number;
};

export type NetEvent = { kind: "netReady" };

const NET_READY: NetEvent[] = [{ kind: "netReady" }];

export function startSoak(soakTime: number): NetState {
  return { soakLeft: soakTime };
}

/** Count a net's soak down; emits `netReady` exactly once, on the crossing. */
export function stepNet(s: NetState, dt: number): NetEvent[] {
  if (s.soakLeft <= 0) return [];
  s.soakLeft -= dt;
  return s.soakLeft <= 0 ? NET_READY : [];
}
